import { myAxios } from '../../util/myAxios';
import { emptyQuestionnaireRequest, QuestionnaireDto, QuestionnaireRequest } from '../questionnaire/questionnaireTypes';
import { Page, Pageable, toUrlParams } from '../../api/pageable';
import { AnsweredQuestionnaireDto } from '../answeredQuestionnaireTypes';
import { AxiosResponse } from 'axios';
import { isValidationErrorResponse, ValidationErrors } from '../../api/restApi';

export function downloadPdf(questionnaireId: number): Promise<Blob> {
  return myAxios()
    .then(axios => axios.get(`/api/v1/pdf/${questionnaireId}`, {responseType: 'blob'}))
    .then(response =>
      new Blob(
        [response.data],
        {type: 'application/pdf'})
    )
}

export function getQuestionnaire(questionnaireId: number): Promise<QuestionnaireDto> {
  return myAxios()
    .then(axios => axios.get(`/api/v1/questionnaire/${questionnaireId}`))
    .then(response => response.data,
      () => emptyQuestionnaireRequest());
}

export function getAnsweredQuestionnaireById(questionnaireId: number): Promise<AnsweredQuestionnaireDto> {
  return myAxios()
    .then(axios => axios.get(`/api/v1/questionnaire/${questionnaireId}/answered`))
    .then(response => response.data,
      () => emptyQuestionnaireRequest())
}

export function getExternalAnsweredQuestionnaireById(questionnaireId: number): Promise<AnsweredQuestionnaireDto> {
  return myAxios()
    .then(axios => axios.get(`/api/v1/questionnaire/${questionnaireId}/answered/external`))
    .then(response => response.data,
      () => emptyQuestionnaireRequest())
}

export function updateQuestionnaire(questionnaire: QuestionnaireDto): Promise<QuestionnaireDto>{
  return myAxios()
    .then(axios => axios.put(`/api/v1/questionnaire/` + questionnaire.id, questionnaire))
    .then(response => response.data,
      error => {
      const response: AxiosResponse = error.response;
      if (isValidationErrorResponse(response)) {
        return response.data;
      } else {
        throw error;
      }
      });
}

export function getSelfActual(): Promise<QuestionnaireDto> {
  return myAxios()
    .then(axios => axios.get(`/api/v1/questionnaire/self-actual`))
    .then(response => response.data,
      () => emptyQuestionnaireRequest());
}

export function getAllQuestionnairesForEvaluation(
  params: { searchText?: string },
  pageable: Pageable): Promise<Page<AnsweredQuestionnaireDto>> {
  const searchParam = params.searchText === undefined ? '' : `search=${encodeURIComponent(params.searchText)}&`;

  return myAxios()
    .then( axios => axios.get(`/api/v1/questionnaire/evaluation?${searchParam}` + toUrlParams(pageable)))
    .then(response => response.data)
}

export function getAllQuestionnaires(
  params: { searchText?: string },
  pageable: Pageable): Promise<Page<QuestionnaireDto>> {
  const searchParam = params.searchText === undefined ? '' : `search=${encodeURIComponent(params.searchText)}&`;

  return myAxios()
    .then( axios => axios.get(`/api/v1/questionnaire?${searchParam}` + toUrlParams(pageable)))
    .then(response => response.data)
}

export function getSelfHistory(
  params: { searchText?: string },
  pageable: Pageable): Promise<Page<AnsweredQuestionnaireDto>> {
  const searchParam = params.searchText === undefined ? '' : `search=${encodeURIComponent(params.searchText)}&`;

  return myAxios()
    .then( axios => axios.get(`/api/v1/questionnaire/self-history?${searchParam}` + toUrlParams(pageable)))
    .then(response => response.data)
}

export function getExternalActual(
  params: { searchText?: string },
  pageable: Pageable): Promise<Page<QuestionnaireDto>> {
  const searchParam = params.searchText === undefined ? '' : `search=${encodeURIComponent(params.searchText)}&`;

  return myAxios()
    .then( axios => axios.get(`/api/v1/questionnaire/external-actual?${searchParam}` + toUrlParams(pageable)))
    .then(response => response.data)
}

export function getExternalHistory(
  params: { searchText?: string },
  pageable: Pageable): Promise<Page<AnsweredQuestionnaireDto>> {
  const searchParam = params.searchText === undefined ? '' : `search=${encodeURIComponent(params.searchText)}&`;

  return myAxios()
    .then( axios => axios.get(`/api/v1/questionnaire/external-history?${searchParam}` + toUrlParams(pageable)))
    .then(response => response.data)
}

export function createQuestionnaire(questionnaire: QuestionnaireRequest): Promise<QuestionnaireDto | ValidationErrors> {
  return myAxios()
    .then( axios => axios.post('/api/v1/questionnaire', questionnaire))
    .then(
      response => response.data,
      error => {
        const response: AxiosResponse = error.response;
        if (isValidationErrorResponse(response)) {
          return response.data;
        } else {
          throw error;
        }
      }
    )
}
